
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

export interface MembershipCoupon {
  id: string;
  membership_purchase_id: string;
  user_coupon_id: string;
  discount_amount: number;
  created_at: string;
  user_coupon: {
    id: string;
    coupon: {
      id: string;
      code: string;
      name: string;
      discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
      discount_value: number;
    };
  };
}

export interface AvailableMembershipCoupon {
  id: string;
  user_id: string;
  coupon_id: string;
  provider_id: string;
  is_active: boolean;
  times_used: number;
  coupon: {
    id: string;
    code: string;
    name: string;
    description?: string;
    discount_type: 'percentage' | 'fixed' | 'weekly_credits' | 'monthly_credits';
    discount_value: number;
    is_active: boolean;
    valid_from: string;
    valid_until?: string;
    provider_id: string;
  };
}

export const useMembershipCoupons = (membershipPurchaseId?: string) => {
  return useQuery({
    queryKey: ['membership-coupons', membershipPurchaseId],
    queryFn: async () => {
      if (!membershipPurchaseId) return [];

      const { data, error } = await supabase
        .from('membership_coupons')
        .select(`
          *,
          user_coupon:user_coupons(
            id,
            coupon:coupons(
              id,
              code,
              name,
              discount_type,
              discount_value
            )
          )
        `)
        .eq('membership_purchase_id', membershipPurchaseId);

      if (error) {
        console.error('Error fetching membership coupons:', error);
        throw error;
      }

      return (data || []) as unknown as MembershipCoupon[];
    },
    enabled: !!membershipPurchaseId,
  });
};

export const useAvailableMembershipCoupons = (providerId?: string) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['available-membership-coupons', providerId, user?.id],
    queryFn: async () => {
      if (!user || !providerId) return [];

      // Get user's active coupons for this provider
      const { data: userCoupons, error } = await supabase
        .from('user_coupons')
        .select(`
          *,
          coupon:coupons(
            id,
            code,
            name,
            description,
            discount_type,
            discount_value,
            is_active,
            valid_from,
            valid_until,
            provider_id
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .eq('provider_id', providerId);

      if (error) {
        console.error('Error fetching user coupons:', error);
        throw error;
      }

      // Filter coupons that are valid and active
      const now = new Date();
      const validCoupons = userCoupons?.filter(userCoupon => {
        const coupon = userCoupon.coupon;
        if (!coupon || !coupon.is_active) return false;

        // Check date validity
        const validFrom = coupon.valid_from ? new Date(coupon.valid_from) : null;
        const validUntil = coupon.valid_until ? new Date(coupon.valid_until) : null;
        
        if (validFrom && now < validFrom) return false;
        if (validUntil && now > validUntil) return false;

        // Only include percentage and fixed discount coupons for memberships
        return coupon.discount_type === 'percentage' || coupon.discount_type === 'fixed';
      }) || [];

      return validCoupons as AvailableMembershipCoupon[];
    },
    enabled: !!user && !!providerId,
  });
};

export const useApplyMembershipCoupon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ membershipPurchaseId, userCouponId, discountAmount }: {
      membershipPurchaseId: string;
      userCouponId: string;
      discountAmount: number;
    }) => {
      const { data, error } = await supabase
        .from('membership_coupons')
        .insert({
          membership_purchase_id: membershipPurchaseId,
          user_coupon_id: userCouponId,
          discount_amount: discountAmount
        })
        .select()
        .single();

      if (error) {
        console.error('Error applying membership coupon:', error);
        throw error;
      }

      // Update the membership purchase with the coupon discount
      const { error: updateError } = await supabase
        .from('membership_purchases')
        .update({
          coupon_discount_amount: discountAmount
        })
        .eq('id', membershipPurchaseId);

      if (updateError) {
        console.error('Error updating membership purchase discount:', updateError);
        throw updateError;
      }

      return data;
    },
    onSuccess: () => {
      toast({
        title: "Coupon Applied",
        description: "Your discount has been applied to the membership purchase.",
      });
      
      queryClient.invalidateQueries({ queryKey: ['membership-coupons'] });
    },
    onError: (error: any) => {
      console.error('Error applying membership coupon:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to apply coupon. Please try again.",
        variant: "destructive",
      });
    },
  });
};
